<template>
	<div class="quote-preview">
		<iframe v-if="pdfUrl && !loading" :src="pdfUrl" frameborder="0"></iframe>
		<div class="centered p-20" v-if="loading">
			<AppLoader :size="36"/>
		</div>
	</div>
</template>

<script>
import {useScriptStore, useLanguageStore} from "@/shared/stores";
import AppLoader from "@/shared/components/AppLoader.vue";

export default {
	setup() {
		const scriptStore = useScriptStore();
		const languageStore = useLanguageStore();
		return {scriptStore, getString: languageStore.getString, readableEurValue: languageStore.readableEurValue};
	},
	name: "QuotePreview",
	components: {AppLoader},
	props: {
		number: {type: Number, default: 1},
		year: {type: Number, default: new Date().getFullYear()},
		date: {type: String, default: ''},
		recipient: {type: String, default: ''},
		subject: {type: String, default: ''},
        quoteLink: {type: String, default: ''},
		items: {type: Array, default: () => []},
	},
    emits: ['generate-quote-link'],
	data() {
		return {
			pdfUrl: null,
			pdfDoc: null,
			loading: true,
		}
	},
	mounted() {
		this.generatePdf();
	},
	watch: {
		quoteLink: {
			handler(newLink, oldLink) {
				if (newLink !== oldLink && newLink) {
					this.generatePdf();
				}
			}
		},
		items: {
			handler(newItems, oldItems) {
				this.generatePdf();
			},
			deep: true
		}
	},
	methods: {
		formatCurrencyForPdf(value, digits = 2) {
			// Get the formatted value from the language store
			const formatted = this.readableEurValue(value, digits);
			// Add a space after the € symbol for better PDF readability
			return formatted.replace('€', '€ ');
		},
		generatePdf() {
			this.scriptStore.loadScript('jsPdf', () => {
				this.scriptStore.loadScript('qrs', () => {
					if (window.jspdf) {
					// Font loading function
					const loadMontserratFonts = async () => {
						const baseUrl = "https://img.qromo.io/fonts/";
						const fonts = [
							{ name: "Montserrat-Regular.ttf", style: "normal" },
							{ name: "Montserrat-Bold.ttf", style: "bold" }
						];
						
						const fontData = {};
						
						for (const font of fonts) {
							try {
								const response = await fetch(baseUrl + font.name);
								if (response.ok) {
									const arrayBuffer = await response.arrayBuffer();
									const bytes = new Uint8Array(arrayBuffer);
									let binaryString = '';
									bytes.forEach((byte) => {
										binaryString += String.fromCharCode(byte);
									});
									fontData[font.style] = { name: font.name, data: binaryString };
								}
							} catch (error) {
								console.error('Error loading font:', font.name, error);
							}
						}
						
						return fontData;
					};
					
					const startPdf = async () => {
						const {jsPDF} = window.jspdf;
						
						// Load Montserrat fonts
						const montserratFonts = await loadMontserratFonts();
						
						// Calculate total content height needed more accurately
						const itemsHeight = this.items.reduce((total, item) => {
							return total + (item.description ? 18 : 12); // 18mm for items with description, 12mm without
						}, 0);
						const headerHeight = 120; // Header section with logo, title, recipient
						const paymentSectionHeight = 60; // Payment methods and bank transfer
						const summaryHeight = 40; // VAT summary section
						const footerHeight = 15; // Footer section (reduced from 30)
						const padding = 10; // Extra padding (reduced from 20)
						
						const totalContentHeight = headerHeight + itemsHeight + paymentSectionHeight + summaryHeight + footerHeight + padding;
						
						// Create PDF with custom dimensions - ensure it's tall enough
						const pageWidth = 210; // A4 width in mm
						const dynamicHeight = Math.max(297, totalContentHeight); // At least A4 height
						
						const doc = new jsPDF({
							orientation: 'portrait',
							unit: 'mm',
							format: [pageWidth, dynamicHeight]
						});
						
						// Add Montserrat fonts to jsPDF
						if (montserratFonts.normal) {
							doc.addFileToVFS(montserratFonts.normal.name, montserratFonts.normal.data);
							doc.addFont(montserratFonts.normal.name, 'Montserrat', 'normal');
						}
						if (montserratFonts.bold) {
							doc.addFileToVFS(montserratFonts.bold.name, montserratFonts.bold.data);
							doc.addFont(montserratFonts.bold.name, 'Montserrat', 'bold');
						}
						
						// Use Montserrat font
						doc.setFont('Montserrat', 'normal');
						
						const pageHeight = doc.internal.pageSize.getHeight();
						const margin = {top: 16, bottom: 20, left: 17, right: 17};
						let y = margin.top;
						const fetchLogo = fetch('https://img.qromo.io/img/logo/color-black-vivo.png')
							.then(r => r.blob())
							.then(b => new Promise(res => {
								const reader = new FileReader();
								reader.onload = () => res(reader.result);
								reader.readAsDataURL(b);
							}))
							.catch(() => null);

						fetchLogo.then(imgData => {
							if (imgData) {
								// Create image to get natural dimensions
								const img = new Image();
								img.onload = () => {
									// Calculate proportional dimensions
									const maxWidth = 32;
									const maxHeight = 12;
									const ratio = Math.min(maxWidth / img.naturalWidth, maxHeight / img.naturalHeight);
									const width = img.naturalWidth * ratio;
									const height = img.naturalHeight * ratio;
									
									doc.addImage(imgData, 'PNG', margin.left, margin.top+2, width, height);
								};
								img.src = imgData;
								
								// Fallback with original dimensions if image load fails
								setTimeout(() => {
									if (!img.complete) {
										doc.addImage(imgData, 'PNG', margin.left, margin.top, 40, 13);
									}
								}, 100);
							}
                            y += 6;
							const headerLines = [
								'CROMO S.R.L.',
								'Via Piero della Francesca 51 – 20154 – Milano (MI)',
								'P. iva 11325140967 – C.F. 11325140967'
							];
							doc.setFontSize(10);
							doc.setFont('Montserrat', 'normal');
							headerLines.forEach((line, i) => {
								doc.text(line, pageWidth - margin.right, y + i * 4, {align: 'right'});
							});

							y += 18;
							doc.setDrawColor(204, 204, 204);
							doc.line(margin.left, y, pageWidth - margin.right, y);
							y += 8;

							doc.setFontSize(14);
							doc.setFont('Montserrat', 'bold');
							doc.text(`PREVENTIVO nr. ${this.number} / ${this.year} – del ${this.date}`, margin.left, y);
							y += 8;

							doc.setFontSize(10);
							doc.setFont('Montserrat', 'normal');
							doc.setTextColor(119, 119, 119);
							doc.text('DESTINATARIO', margin.left, y);
							y += 5;
							doc.setFont('Montserrat', 'bold');
							doc.setFontSize(13);
							doc.setTextColor(0, 0, 0);
							doc.text(this.recipient || '-', margin.left, y);
							y += 4;

							doc.setDrawColor(187, 187, 187);
							doc.line(margin.left, y, pageWidth - margin.right, y);
							y += 8;

                            doc.setFont('Montserrat', 'bold');
                            doc.setFontSize(10);
                            doc.text('PAGAMENTO CON CARTA O A RATE', margin.left, y);
                            y += 6;
                            doc.setFont('Montserrat', 'normal');
                            doc.setFontSize(10);
                            doc.setTextColor(0, 102, 204); // Blue color (Qromo blue)
                            [this.quoteLink].forEach(l => {
                                // Add underline by drawing a line below the text
                                const textWidth = doc.getTextWidth(l);
                                doc.text(l, margin.left, y);
                                // Draw underline
                                doc.setDrawColor(0, 102, 204); // Same blue color for underline
                                doc.line(margin.left, y + 1, margin.left + textWidth, y + 1);
                                y += 5;
                            });
                            
                            // Reset text color to black
                            doc.setTextColor(0, 0, 0);

                            // Store positions for payment logos and QR code
                            let paymentLogosPosition = null;
                            let qrPosition = null;
                            if (this.quoteLink && window.QRCode && this.quoteLink.startsWith('http')) {
                                paymentLogosPosition = y; // Payment logos go first
                                y += 25; // Space for payment logos + bank transfer section
                                qrPosition = y; // QR code below payment logos and bank transfer
                            }

                            const addQrCodeAndFinalize = () => {
                                if (qrPosition && this.quoteLink && window.QRCode && this.quoteLink.startsWith('http')) {
                                    // First add payment logos above QR code
                                    addPaymentLogos(paymentLogosPosition, () => {
                                        // Then generate QR code
                                        const qrContainer = document.createElement('div');
                                        qrContainer.style.position = 'absolute';
                                        qrContainer.style.left = '-9999px';
                                        document.body.appendChild(qrContainer);

                                        try {
                                            const qr = new window.QRCode(qrContainer, {
                                                text: this.quoteLink,
                                                width: 120,
                                                height: 120,
                                                colorDark: '#000000',
                                                colorLight: '#ffffff',
                                                correctLevel: window.QRCode.CorrectLevel.M,
                                                dotScale: 1
                                            });

                                            // Wait briefly and add QR to saved position
                                            setTimeout(() => {
                                                const canvas = qrContainer.querySelector('canvas');
                                                if (canvas) {
                                                    const qrDataUrl = canvas.toDataURL();
                                                    // Align QR code to the right and move up by 20px
                                                    const qrSize = 20;
                                                    const qrX = pageWidth - margin.right - qrSize;
                                                    const qrY = qrPosition - 38.5; // Move up by 20px
                                                    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
                                                }
                                                document.body.removeChild(qrContainer);
                                                finalizePdf();
                                            }, 200);
                                        } catch (e) {
                                            document.body.removeChild(qrContainer);
                                            finalizePdf();
                                        }
                                    });
                                } else {
                                    finalizePdf();
                                }
                            };

                            // Function to add payment method logos
                            const addPaymentLogos = (startY, callback) => {
                                const logoUrls = [
                                    'https://img.qromo.io/img/logo-apple-pay.png',
                                    'https://img.qromo.io/img/logo-google-pay.png',
                                    'https://img.qromo.io/img/logo-master-card.png',
                                    'https://img.qromo.io/img/logo-visa.png',
                                    'https://img.qromo.io/img/logo-klarna.png'
                                ];

                                let loadedLogos = 0;
                                const logoData = [];

                                const loadNextLogo = (index) => {
                                    if (index >= logoUrls.length) {
                                        // All logos loaded, add them to PDF aligned to left
                                        let currentX = margin.left;

                                        logoData.forEach((logoInfo, i) => {
                                            if (logoInfo) {
                                                doc.addImage(logoInfo.data, 'PNG', currentX, startY, logoInfo.width, logoInfo.height);
                                                currentX += logoInfo.width + 2; // Add 2mm spacing between logos
                                            }
                                        });

                                        // Add bank transfer payment section after logos
                                        let bankY = startY + 12; // Position below the logos
                                        
                                        doc.setFont('Montserrat', 'bold');
                                        doc.setFontSize(10);
                                        doc.text('PAGAMENTO TRAMITE BONIFICO', margin.left, bankY);
                                        bankY += 5;
                                        
                                        doc.setFont('Montserrat', 'normal');
                                        doc.setFontSize(9);
                                        const bankDetails = [
                                            'Bonifico Bancario',
                                            'IBAN: IT82 Q360 9201 6003 4122 8236 699',
                                            'Intestatario: CROMO SRL',
                                            'Banca: Qonto',
                                            'BIC: QNTOITM2XXX'
                                        ];
                                        
                                        bankDetails.forEach(detail => {
                                            doc.text(detail, margin.left, bankY);
                                            bankY += 4.5;
                                        });

                                        // Call callback when done instead of finalizePdf
                                        if (callback) {
                                            callback();
                                        } else {
                                            finalizePdf();
                                        }
                                        return;
                                    }

                                    // Create image element to load with CORS support
                                    const img = new Image();
                                    img.crossOrigin = 'anonymous';
                                    img.onload = () => {
                                        // Convert image to canvas keeping original dimensions
                                        const canvas = document.createElement('canvas');
                                        const ctx = canvas.getContext('2d');
                                        canvas.width = img.naturalWidth;
                                        canvas.height = img.naturalHeight;
                                        ctx.drawImage(img, 0, 0);

                                        // Calculate proportional dimensions for PDF (max height 5mm)
                                        const maxHeight = 5;
                                        const ratio = maxHeight / (img.naturalHeight * 0.264583); // Convert pixels to mm
                                        const pdfWidth = (img.naturalWidth * 0.264583) * ratio;
                                        const pdfHeight = maxHeight;

                                        try {
                                            logoData[index] = {
                                                data: canvas.toDataURL('image/png'),
                                                width: pdfWidth,
                                                height: pdfHeight
                                            };
                                        } catch (e) {
                                            logoData[index] = null;
                                        }

                                        loadNextLogo(index + 1);
                                    };
                                    img.onerror = () => {
                                        logoData[index] = null;
                                        loadNextLogo(index + 1);
                                    };
                                    img.src = logoUrls[index];
                                };

                                loadNextLogo(0);
                            };
                            y += 16;
                            doc.setDrawColor(204, 204, 204);
                            doc.line(margin.left, y, pageWidth - margin.right, y);
                            y += 8;

							doc.setFont('Montserrat', 'normal');
							doc.setFontSize(10);
							doc.setTextColor(119, 119, 119);
							doc.text('OGGETTO', margin.left, y);
							y += 5;
							doc.setFont('Montserrat', 'bold');
							doc.setFontSize(12);
							doc.setTextColor(0, 0, 0);
							
							// Support multiline subject
							const subjectText = this.subject || '-';
							const subjectLines = doc.splitTextToSize(subjectText, pageWidth - margin.left - margin.right);
							subjectLines.forEach((line, lineIndex) => {
								doc.text(line, margin.left, y + (lineIndex * 4));
							});
							y += (subjectLines.length * 4) + 2;

							const tableX = margin.left;
							const tableW = pageWidth - margin.left - margin.right;
							const colPerc = [46, 11, 11, 14, 18];
							const colW = colPerc.map(p => tableW * p / 100);
							const colX = [tableX];
							for (let i = 1; i < colW.length; i++) {
								colX[i] = colX[i - 1] + colW[i - 1];
							}

							const headerY = y + 4;
							doc.setFillColor(240, 230, 247); // Purple color
							doc.roundedRect(tableX, headerY - 6, tableW, 8, 1.8, 1.8, 'F');
							doc.setFont('Montserrat', 'bold');
							doc.setFontSize(7);
							doc.setTextColor(51, 51, 51);
							const headers = ['Descrizione', 'Quantità', 'Sconto', 'Prezzo unitario', 'Importo'];
							headers.forEach((h, i) => {
								if (i === 3 || i === 4) { // Prezzo unitario e Importo (ora indici 3 e 4)
									const rightPadding = i === 4 ? 3 : 1; // Più padding per Importo (ultima colonna)
									doc.text(h.toUpperCase(), colX[i] + colW[i] - rightPadding, headerY, {align: 'right'});
								} else {
									const leftPadding = 3; // Padding standard per tutte le colonne di sinistra
									doc.text(h.toUpperCase(), colX[i] + leftPadding, headerY);
								}
							});
							y = headerY + 2;
							doc.setFont('Montserrat', 'normal');
							doc.setFontSize(12);
							doc.setTextColor(0, 0, 0);

							this.items.forEach((it, idx) => {
								// Calculate title and description lines for dynamic row height
								doc.setFontSize(12);
								const titleLines = doc.splitTextToSize(it.title, colW[0] - 6);
								
								let descriptionLines = [];
								if (it.description) {
									doc.setFontSize(10);
									descriptionLines = doc.splitTextToSize(it.description, colW[0] - 6);
									doc.setFontSize(12);
								}
								
								// Dynamic row height based on content
								const titleLineHeight = 4; // Increased line height for titles
								const descriptionLineHeight = 3.5; // Height per line for descriptions
								const titleHeight = titleLines.length * titleLineHeight;
								const descriptionHeight = descriptionLines.length * descriptionLineHeight;
								const contentHeight = titleHeight + descriptionHeight + (descriptionLines.length > 0 ? 1 : 0); // Extra spacing if description exists
								const padding = 2; // Top and bottom padding
								const rowHeight = Math.max(12, contentHeight + padding);
								
								y += rowHeight;
								
								// Background alternato per righe pari/dispari
								if (idx % 2 === 1) { // Righe dispari (1, 3, 5...)
									doc.setFillColor(250, 248, 255); // Light purple background
									doc.rect(tableX, y - rowHeight, tableW, rowHeight, 'F'); // Allineato con l'altezza della riga
								}
								
								doc.setDrawColor(221, 221, 221);
								doc.line(tableX, y, tableX + tableW, y);
								const subtotal = it.price * it.quantity;
								const discountAmount = subtotal * (it.discount || 0) / 100;
								const amount = subtotal - discountAmount;
								
								// Calculate vertical position for text alignment
								// For items with description, center the title+description group
								const topPadding = (rowHeight - contentHeight) / 2;
								const titleY = (titleLines.length > 1 || descriptionLines.length > 0) ? y - rowHeight + topPadding + titleLineHeight : y - (rowHeight / 2) + 2;
								const descriptionY = titleY + titleHeight + 0.5; // Add 0.5 extra space between title and description
								// Other columns should always be vertically centered in the row
								const textY = y - (rowHeight / 2) + 2;
								
								// Title (multiline support)
								doc.setFont('Montserrat', 'normal');
								doc.setFontSize(12);
								doc.setTextColor(0, 0, 0);
								titleLines.forEach((line, lineIndex) => {
									doc.text(line, colX[0] + 3, titleY + (lineIndex * titleLineHeight));
								});
								
								// Description (if exists)
								if (it.description && descriptionLines.length > 0) {
									doc.setFontSize(10);
									doc.setTextColor(100, 100, 100);
									// Render each line of the description
									descriptionLines.forEach((line, lineIndex) => {
										doc.text(line, colX[0] + 3, descriptionY + (lineIndex * descriptionLineHeight));
									});
									doc.setFontSize(12);
									doc.setTextColor(0, 0, 0);
								}
								
								doc.text(String(it.quantity), colX[1] + colW[1] / 2, textY, {align: 'center'});
								doc.text(it.discount ? `${it.discount}%` : '', colX[2] + colW[2] / 2, textY, {align: 'center'});
								
								// Handle price display with discount
								if (it.discount && it.discount > 0) {
									// Show original price crossed out (centered above)
									const originalPriceText = this.formatCurrencyForPdf(it.price);
									const originalPriceX = colX[3] + colW[3] - 1;
									const originalPriceY = textY - 3; // Better vertical centering
									
									doc.setFontSize(10);
									doc.setTextColor(120, 120, 120); // Gray color for crossed price
									doc.text(originalPriceText, originalPriceX, originalPriceY, {align: 'right'});
									
									// Draw line through the original price
									const originalPriceWidth = doc.getTextWidth(originalPriceText);
									doc.setDrawColor(120, 120, 120);
									doc.line(originalPriceX - originalPriceWidth, originalPriceY - 1, originalPriceX, originalPriceY - 1);
									
									// Show discounted price below (centered below)
									const discountedPrice = it.price * (1 - it.discount / 100);
									doc.setFontSize(12);
									doc.setTextColor(0, 0, 0); // Black color for discounted price
									doc.text(this.formatCurrencyForPdf(discountedPrice), originalPriceX, textY + 1.5, {align: 'right'});
								} else {
									// No discount, show normal price
									doc.text(this.formatCurrencyForPdf(it.price), colX[3] + colW[3] - 1, textY, {align: 'right'});
								}
								
								doc.setFont('Montserrat', 'bold');
								doc.text(this.formatCurrencyForPdf(amount), colX[4] + colW[4] - 3, textY, {align: 'right'}); // Più padding per Importo
								doc.setFont('Montserrat', 'normal');
							});
							y += 16;

							const sumNet = this.items.reduce((a, b) => {
								const itemTotal = b.price * b.quantity;
								const discountAmount = itemTotal * (b.discount || 0) / 100;
								return a + itemTotal - discountAmount;
							}, 0);
							const sumVat = sumNet * 0.22;
							const sumTot = sumNet + sumVat;
							const boxW = 60;
							const xBox = pageWidth - margin.right - boxW;
							doc.setFont('Montserrat', 'normal');
							doc.setFontSize(10);
							doc.setTextColor(119, 119, 119);
							doc.text('RIEPILOGO IVA ', xBox + boxW, y, {align: 'right'});
							y += 8;
							doc.setFontSize(12);
							doc.setTextColor(0, 0, 0);
							doc.text('Imponibile ' + this.formatCurrencyForPdf(sumNet), xBox + boxW, y, {align: 'right'});
							y += 6;
							doc.text('Totale IVA ' + this.formatCurrencyForPdf(sumVat), xBox + boxW, y, {align: 'right'});
							y += 4;
							doc.setDrawColor(187, 187, 187);
							doc.line(xBox, y, xBox + boxW, y);
							y += 8;
							doc.setFont('Montserrat', 'bold');
							doc.setFontSize(16);
							doc.text(this.formatCurrencyForPdf(sumTot), xBox + boxW, y, {align: 'right'});

							const finalizePdf = () => {
								// Position footer closer to content
								const footerY = y + 8; // Reduced spacing to 8mm
								const finalFooterY = footerY + 8; // Footer text position
								
								// Add footer to current doc
								doc.setDrawColor(204, 204, 204);
								doc.line(margin.left, footerY, pageWidth - margin.right, footerY);
								doc.setFont('Montserrat', 'normal');
								doc.setFontSize(10);
								doc.setTextColor(85, 85, 85);
								doc.text(`Preventivo nr. ${this.number} del ${this.date} – 1/1`, margin.left, finalFooterY);
								doc.text('qromo.it – info@qromo.it', pageWidth - margin.right, finalFooterY, {align: 'right'});

								const blob = doc.output('blob');
								if (this.pdfUrl) {
									URL.revokeObjectURL(this.pdfUrl);
								}
								this.$nextTick(()=>{
									this.pdfUrl = URL.createObjectURL(blob);
									this.pdfDoc = doc;
									this.loading = false;
									this.$emit('generated', doc);
								});
							};

							addQrCodeAndFinalize();
						});
					};
					startPdf();
					} else {
						this.loading = false;
					}
				});
			});
		},
		download() {
			if (this.pdfDoc) {
				this.pdfDoc.save('quote.pdf');
			}
		}
	},
	beforeUnmount() {
		if (this.pdfUrl) {
			URL.revokeObjectURL(this.pdfUrl);
		}
	}
}
</script>

<style scoped lang="scss">
.quote-preview {
	width: 100%;
	height: calc(100vh - 200px);
	overflow: hidden;

	iframe {
		width: 100%;
		height: 100%;
	}
}
</style>
